import { types as api } from '@mesa-labs/mesa-api';
import { DateTime } from 'luxon';

import { DateRange, isDateRangeSet, isRangeSet, NumberRange } from './enablementV2Filters';
import { parseStoredSavedFilters, SavedFilter } from './savedFilters';

export const VendorInvoiceOptionFilterKeys = ['partners', 'clients', 'programs', 'collectionSchemes', 'states'] as const;
export type VendorInvoiceOptionFilterKey = typeof VendorInvoiceOptionFilterKeys[number];

export const VendorInvoiceDateFilterKeys = [
  'invoiceDate', 'createdAt', 'dueDate', 'eligibleAt', 'fundedAt', 'collectionDate', 'settledAt',
] as const;
export type VendorInvoiceDateFilterKey = typeof VendorInvoiceDateFilterKeys[number];

export const VendorInvoiceDateFilterLabels: Record<VendorInvoiceDateFilterKey, string> = {
  invoiceDate: 'Invoice Date',
  createdAt: 'Import Date',
  dueDate: 'Due Date',
  eligibleAt: 'Eligible',
  fundedAt: 'Funded',
  collectionDate: 'Collection Date',
  settledAt: 'Settled',
};

export type VendorInvoiceFilterSet = {
  readonly search?: string;
  readonly partners: readonly api.Partners[];
  /** Client option values; see toClientFilterValue. */
  readonly clients: readonly string[];
  readonly programs: readonly api.ProgramCodes[];
  readonly collectionSchemes: readonly api.InvoiceCollectionScheme[];
  readonly states: readonly api.InvoiceState[];
  readonly hasExternalPayStatus?: boolean;
  readonly amount?: NumberRange;
  readonly dates: Partial<Record<VendorInvoiceDateFilterKey, DateRange>>;
};

export const EmptyVendorInvoiceFilterSet: VendorInvoiceFilterSet = {
  search: undefined,
  partners: [],
  clients: [],
  programs: [],
  collectionSchemes: [],
  states: [],
  hasExternalPayStatus: undefined,
  amount: undefined,
  dates: {},
};

// A client is identified by (partnerId, externalClientId), so its option value carries both.
const ClientFilterValueSeparator = ':';

export const toClientFilterValue = (partnerId: api.Partners, externalClientId: string): string => (
  `${partnerId}${ClientFilterValueSeparator}${externalClientId}`
);

export const fromClientFilterValue = (value: string): { partnerId: api.Partners; externalClientId: string } => {
  const index = value.indexOf(ClientFilterValueSeparator);
  return { partnerId: Number(value.slice(0, index)), externalClientId: value.slice(index + 1) };
};

/** Parses a FilterBar option value (always a string) back to the filter's stored value. */
const parseOptionValue = (key: VendorInvoiceOptionFilterKey, value: string): string | number => (
  key === 'partners' ? Number(value) : value
);

export const addOptionFilterValue = (
  filters: VendorInvoiceFilterSet,
  key: VendorInvoiceOptionFilterKey,
  value: string,
): VendorInvoiceFilterSet => {
  const current = filters[key] as readonly (string | number)[];
  const parsed = parseOptionValue(key, value);
  return current.includes(parsed) ? filters : { ...filters, [key]: [...current, parsed] };
};

export const removeOptionFilterValue = (
  filters: VendorInvoiceFilterSet,
  key: VendorInvoiceOptionFilterKey,
  value: string,
): VendorInvoiceFilterSet => {
  const parsed = parseOptionValue(key, value);
  return { ...filters, [key]: (filters[key] as readonly (string | number)[]).filter((v) => v !== parsed) };
};

export const toActiveFilters = (filters: VendorInvoiceFilterSet): readonly { category: string; value: string }[] => (
  VendorInvoiceOptionFilterKeys.flatMap((key) => (filters[key] as readonly (string | number)[])
    .map((value) => ({ category: key, value: String(value) })))
);

/** Search is excluded: the table's search box shows it and counts it separately. */
export const countActiveVendorInvoiceFilters = (filters: VendorInvoiceFilterSet): number => (
  VendorInvoiceOptionFilterKeys.reduce((sum, key) => sum + (filters[key].length > 0 ? 1 : 0), 0)
  + (filters.hasExternalPayStatus ? 1 : 0)
  + (isRangeSet(filters.amount) ? 1 : 0)
  + VendorInvoiceDateFilterKeys.reduce((sum, key) => sum + (isDateRangeSet(filters.dates[key]) ? 1 : 0), 0)
);

type DateBounds = {
  readonly beginningAt?: api.ISODateString;
  readonly endingAt?: api.ISODateString;
};

/** Inclusive calendar dates in `zone` → [start of after, end of before] instants. */
const toDateBounds = (range: DateRange | undefined, zone: string): DateBounds => ({
  beginningAt: range?.after ? DateTime.fromISO(range.after, { zone }).startOf('day').toISO() as api.ISODateString : undefined,
  endingAt: range?.before ? DateTime.fromISO(range.before, { zone }).endOf('day').toISO() as api.ISODateString : undefined,
});

const toDateAdvancedFilters = (field: string, range: DateRange | undefined, zone: string): api.InvoiceAdvancedFilter[] => {
  const { beginningAt, endingAt } = toDateBounds(range, zone);
  return [
    ...(beginningAt ? [{ field, operator: api.InvoiceComparisonOperator.GTE, value: beginningAt }] : []),
    ...(endingAt ? [{ field, operator: api.InvoiceComparisonOperator.LTE, value: endingAt }] : []),
  ];
};

const toInclusionFilter = (field: string, values: readonly (string | number)[]): api.InvoiceAdvancedFilter[] => (
  values.length > 0
    ? [{ field, operator: api.InvoiceInclusionOperator.IN, value: values as readonly string[] | readonly number[] }]
    : []
);

/**
 * Converts the filter set to GET /invoices params. Dates are calendar days in `zone`. Partner,
 * client, program, collection method, due date and collection date have no dedicated params, so they
 * go through advancedFilters. States are left to the caller, which defaults excludeStates when none
 * are selected.
 */
export const toVendorInvoiceFilterParams = (
  filters: VendorInvoiceFilterSet,
  zone: string,
): Partial<api.InvoiceFilterParams> => {
  const invoiceDate = toDateBounds(filters.dates.invoiceDate, zone);
  const createdAt = toDateBounds(filters.dates.createdAt, zone);
  const eligibleAt = toDateBounds(filters.dates.eligibleAt, zone);
  const fundedAt = toDateBounds(filters.dates.fundedAt, zone);
  const settledAt = toDateBounds(filters.dates.settledAt, zone);

  const advancedFilters = [
    ...toInclusionFilter('partnerId', filters.partners),
    // Client values are unique per partner, but externalClientId alone is what the API can filter on;
    // the partner filter narrows it further when both are set.
    ...toInclusionFilter('externalClientId', [...new Set(filters.clients.map((c) => fromClientFilterValue(c).externalClientId))]),
    ...toInclusionFilter('configurationProgramCode', filters.programs),
    ...toInclusionFilter('collectionScheme', filters.collectionSchemes),
    ...toDateAdvancedFilters('dueDate', filters.dates.dueDate, zone),
    ...toDateAdvancedFilters('collectionDate', filters.dates.collectionDate, zone),
  ];

  return {
    search: filters.search || undefined,
    includeStates: filters.states.length > 0 ? filters.states : undefined,
    hasExternalPayStatus: filters.hasExternalPayStatus || undefined,
    minAmount: filters.amount?.low,
    maxAmount: filters.amount?.high,
    beginningAt: invoiceDate.beginningAt,
    endingAt: invoiceDate.endingAt,
    createdBeginningAt: createdAt.beginningAt,
    createdEndingAt: createdAt.endingAt,
    eligibleBeginningAt: eligibleAt.beginningAt,
    eligibleEndingAt: eligibleAt.endingAt,
    fundedBeginningAt: fundedAt.beginningAt,
    fundedEndingAt: fundedAt.endingAt,
    settledBeginningAt: settledAt.beginningAt,
    settledEndingAt: settledAt.endingAt,
    advancedFilters: advancedFilters.length > 0 ? JSON.stringify(advancedFilters) as api.JSONString : undefined,
  };
};

const arrayOf = <T>(value: unknown, isValid: (v: unknown) => v is T): readonly T[] => (
  Array.isArray(value) ? value.filter(isValid) : []
);

const isEnumValue = <T extends string>(enumObject: Record<string, T>) => (v: unknown): v is T => (
  Object.values(enumObject).includes(v as T)
);

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isString = (v: unknown): v is string => typeof v === 'string';

const toNumberRange = (value: unknown): NumberRange | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const { low, high } = value as Record<string, unknown>;
  const range = { low: isNumber(low) ? low : undefined, high: isNumber(high) ? high : undefined };
  return isRangeSet(range) ? range : undefined;
};

const toDateRangeValue = (value: unknown): DateRange | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const { after, before } = value as Record<string, unknown>;
  const range = { after: isString(after) && after ? after : undefined, before: isString(before) && before ? before : undefined };
  return isDateRangeSet(range) ? range : undefined;
};

/**
 * Normalizes a stored (persisted or saved) filter set: back-fills missing keys and drops values that
 * no longer parse, e.g. an enum value removed from mesa-api.
 */
export const normalizeVendorInvoiceFilterSet = (stored: unknown): VendorInvoiceFilterSet => {
  if (typeof stored !== 'object' || stored === null) return EmptyVendorInvoiceFilterSet;
  const raw = stored as Record<string, unknown>;
  const rawDates = (typeof raw.dates === 'object' && raw.dates !== null ? raw.dates : {}) as Record<string, unknown>;
  return {
    search: isString(raw.search) && raw.search ? raw.search : undefined,
    partners: arrayOf(raw.partners, isNumber),
    clients: arrayOf(raw.clients, isString),
    programs: arrayOf(raw.programs, isEnumValue(api.ProgramCodes)),
    collectionSchemes: arrayOf(raw.collectionSchemes, isEnumValue(api.InvoiceCollectionScheme)),
    states: arrayOf(raw.states, isEnumValue(api.InvoiceState)),
    hasExternalPayStatus: raw.hasExternalPayStatus === true || undefined,
    amount: toNumberRange(raw.amount),
    dates: VendorInvoiceDateFilterKeys.reduce<Partial<Record<VendorInvoiceDateFilterKey, DateRange>>>((dates, key) => {
      const range = toDateRangeValue(rawDates[key]);
      return range ? { ...dates, [key]: range } : dates;
    }, {}),
  };
};

// ─── Saved filters (per user, localStorage only) ────────────────────────────

export type VendorInvoiceSavedFilter = SavedFilter<VendorInvoiceFilterSet>;

export const vendorInvoiceSavedFiltersStorageKey = (email: string | undefined): string => (
  `mesa.vendorInvoices.savedFilters.v1:${email ?? 'anonymous'}`
);

export const parseVendorInvoiceSavedFilters = (raw: string | null): readonly VendorInvoiceSavedFilter[] => (
  parseStoredSavedFilters(raw, (stored: unknown) => normalizeVendorInvoiceFilterSet(stored))
);
